import React, { useEffect, useState } from 'react';
import { Link } from '@naf-simba/elements-links';
import { useDispatch } from 'react-redux';
import { Button } from '@naf-simba/elements-buttons';
import { Grid, GridCol } from '@naf/grid';
import { Text, TextVariant } from '@naf/text';

import type { SanityBlock } from '@sanity/client';
import { ProductType } from '@dtp/membership-service-types';
import { actions } from '../../../redux/modules/myMembership';
import useSelector from '../../../redux/typedHooks';
import { useWindowLocation } from '../../../hooks/Window/useWindowLocation';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { useLoginRedirect } from '../../../hooks/useLoginRedirect';
import BlockContent from '../../../components/block-content/BlockContent';
import ResponsiveImage from '../../../components/image/ResponsiveImage';
import { IMAGE_SIZES } from '../../../components/image/ResponsiveImage.sizes';
import { QuoteBlock } from '../../../components/block-content/types/components/Quote/Quote';
import { AccordionBlock } from '../../../components/block-content/types/components/Accordion/Accordion';
import { CampaignBlock } from '../../../components/block-content/types/components/CampaignBlock/CampaignBlock';
import { LayoutWithMainContent } from '../../../components/layout/Layout';
import { ProductArticleType } from '../../../../../types/articleType';
import { actions as idpActions } from '../../../redux/modules/internationalDrivingPermit';
import { useDocumentData } from '../../../hooks/Documents/useDocumentData';
import { InternalSearchCardType } from '../../../components/layout/InternalSearchMetaData';
import { NotFound } from '../../../loadable-elements/NotFound';
import BenefitsList from '../../BenefitArticle/NewRelatedBenefits/BenefitList/BenefitList';
import { LoaderContent } from '../../LoaderPage';
import { useCloudinary } from '../../../hooks/Cloudinary/useCloudinary';
import * as S from './InternationalDrivingPermitArticle.styles';

const slug = 'internasjonalt-forerkort';

export const IdpArticle = () => {
  const dispatch = useDispatch();
  const windowPath = useWindowLocation();
  const { getProcessedImage } = useCloudinary();
  const { redirectToLoginPage } = useLoginRedirect();
  const { simpleToken, richToken } = useAuth0Token();
  const products = useSelector((state) => state.internationalDrivingPermit.data.eligibleProducts || null);
  const {
    customerInformation,
    customerInformation: {
      meta: { fetchState },
    },
  } = useSelector((state) => state.myMembership);
  const isMember = customerInformation?.data?.membership?.isValid || false;
  const [didFetchProducts, setDidFetchProducts] = useState<boolean>(false);
  const [didFetchMemberPrice, setDidFetchMemberPrice] = useState<boolean>(false);
  const [product, setProduct] = useState<ProductType>();
  const [isLoggedin, setIsLoggedin] = useState<boolean>(false);
  const {
    data: productArticle,
    meta: { isUpdating: isUpdatingArticle },
  } = useDocumentData(slug, simpleToken) as {
    data: ProductArticleType;
    meta: {
      isUpdating?: boolean;
      fetchedAt?: string;
      isLoggedin?: boolean;
    };
  };

  useEffect(() => {
    if (!products && !didFetchProducts) {
      dispatch(idpActions.getInternationalDrivingPermit.request(undefined));
      setDidFetchProducts(true);
    }
    if (!didFetchMemberPrice && isLoggedin && simpleToken) {
      dispatch(idpActions.getInternationalDrivingPermit.request(undefined));
      setDidFetchMemberPrice(true);
      setDidFetchProducts(true);
    }
  }, [didFetchMemberPrice, didFetchProducts, products, simpleToken, isLoggedin, dispatch]);

  useEffect(() => {
    if (products && productArticle && productArticle.products && productArticle.products[0]) {
      setProduct(products[productArticle.products[0].productId]);
    } else setProduct(undefined);
  }, [products, productArticle]);

  useEffect(() => {
    if (richToken && fetchState === 'initial') {
      dispatch(actions.getCustomerInformation.request(richToken));
    }
  }, [richToken, dispatch, fetchState]);

  useEffect(() => {
    if (typeof customerInformation?.data?.membership !== 'undefined' && fetchState === 'success') {
      setIsLoggedin(true);
    } else setIsLoggedin(false);
  }, [customerInformation, fetchState]);

  if (!isUpdatingArticle && !productArticle) return <NotFound />;
  if (!productArticle) return <LoaderContent />;

  const { name, header, ingress, body, _id, image, relatedProduct, productSummary, quote, campaignBlock, seoConfig } =
    productArticle;

  const seoTitle = seoConfig?.title ? seoConfig.title : name;
  const primaryCtaUrl = relatedProduct?.ctaButtonLink?.url;
  const primaryCtaText =
    isLoggedin && isMember ? 'Bestill internasjonalt førerkort' : relatedProduct?.ctaButtonLink?.text;
  const secondaryCtaUrl = relatedProduct?.ctaSecondaryButtonLink?.url;
  const schemaImage = getProcessedImage(image?.publicId, {
    height: 630,
    width: 1200,
    focusMethod: image?.focus,
  })?.toURL();

  return (
    <LayoutWithMainContent
      title={seoTitle}
      description={seoConfig?.introduction}
      imgUrl={schemaImage || ''}
      url={windowPath}
      gtmArgs={{
        page_type: 'ProductArticlePage',
        pageCategory: `medlemskap/${slug}`,
        contentId: _id,
      }}
      seoTitle={seoTitle}
      seoDescription={seoConfig?.introduction}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Product,
        product: {
          title: 'Internasjonalt førerkort',
          price: product?.productAppPrice['1']?.itemTotalPrice?.toFixed(10),
        },
      }}
    >
      <S.Wrapper>
        <Grid>
          <S.HeaderGridRow>
            <S.HeaderCol s="12" m="12" l="6" xl="6">
              <Text tag="h1" variant={TextVariant.Header1}>
                {header || name}
              </Text>
              {relatedProduct && (
                <>
                  {relatedProduct.productHeaderText && (
                    <Text tag="h3" variant={TextVariant.Header3}>
                      {relatedProduct.productHeaderText}
                    </Text>
                  )}
                  {relatedProduct.productText && (
                    <Text variant={TextVariant.ArticleText}>{relatedProduct.productText}</Text>
                  )}
                  <S.ButtonContainer>
                    {primaryCtaUrl && primaryCtaText && (
                      <Button width="fit-content" to={primaryCtaUrl}>
                        {primaryCtaText}
                      </Button>
                    )}
                    {!isMember && relatedProduct.ctaSecondaryButtonLink && secondaryCtaUrl && (
                      <Button
                        width="fit-content"
                        variant="outlined"
                        onClick={() => redirectToLoginPage(secondaryCtaUrl)}
                      >
                        {relatedProduct.ctaSecondaryButtonLink.text}
                      </Button>
                    )}
                  </S.ButtonContainer>
                </>
              )}
            </S.HeaderCol>
            {image && (
              <S.ImageCol s="12" m="12" l="6" xl="6">
                <ResponsiveImage
                  imageId={image.publicId}
                  altText={image.altText || ''}
                  quality="auto:good"
                  sizes={IMAGE_SIZES.twoColumn}
                />
              </S.ImageCol>
            )}
          </S.HeaderGridRow>
          {ingress && (
            <S.IngressCol s="12" m="12" l="7" xl="7">
              <Text variant={TextVariant.Ingress}>{ingress}</Text>
            </S.IngressCol>
          )}
          <S.SpacedGridRow>
            <GridCol s="12" m="12" l="8" xl="8">
              {body && (
                <S.Body>
                  <BlockContent value={body} />
                </S.Body>
              )}
            </GridCol>
          </S.SpacedGridRow>
          {productSummary && productSummary.summarylist && productSummary.summarylist.length > 0 && (
            <S.SpacedGridRow>
              <GridCol s="12" m="12" l="8" xl="8">
                <S.ColoredBox>
                  {productSummary.header && (
                    <Text tag="h2" variant={TextVariant.Header2}>
                      {productSummary.header}
                    </Text>
                  )}
                  {productSummary.summarylist.map((accordion: { text: SanityBlock[]; title: string; type: string }) => (
                    <AccordionBlock key={accordion.title} value={accordion} />
                  ))}
                </S.ColoredBox>
              </GridCol>
            </S.SpacedGridRow>
          )}
          {quote && quote.text && (
            <S.SpacedGridRow>
              <S.QuoteCol s="12" m="12" l="12" xl="12">
                <QuoteBlock value={quote} />
              </S.QuoteCol>
            </S.SpacedGridRow>
          )}
          {campaignBlock && (
            <GridCol s="12" m="12" l="12" xl={campaignBlock.illustration ? '12' : '11'}>
              <CampaignBlock value={campaignBlock} />
            </GridCol>
          )}
          {productArticle.membershipBenefits && (
            <GridCol s="12" m="12" l="12" xl="12">
              <S.ReadMoreWrapper>
                <Text variant={TextVariant.Header2}>Medlemsfordeler</Text>
                <Link text="Se alle" to="/medlemskap/medlemsfordeler" />
              </S.ReadMoreWrapper>
              <BenefitsList benefitItems={productArticle.membershipBenefits} />
            </GridCol>
          )}
        </Grid>
      </S.Wrapper>
    </LayoutWithMainContent>
  );
};
